import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "@/i18n";

const IMG_LARGE =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/otb1dopj_760916310_27402174559484526_4844914775450722204_n.jpg";
const IMG_A =
  "https://images.unsplash.com/photo-1729626739623-7f39861a1f60?fm=jpg&q=80&w=800&auto=format&fit=crop";
const IMG_B =
  "https://images.unsplash.com/photo-1658566658763-5e7c1f2fb925?fm=jpg&q=80&w=800&auto=format&fit=crop";

const EASE = [0.16, 1, 0.3, 1];

export default function SailingFleet() {
  const { t } = useLang();
  const c = t.cabinati;
  return (
    <section id="cabinati" data-testid="cabinati-section" className="bg-glacier py-24 sm:py-32 grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-4">{c.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[1.08] text-lake-deep">
            {c.title}
          </h2>
          <p className="mt-5 text-lake-deep/70 text-base sm:text-lg leading-relaxed">{c.sub}</p>
          <ul className="mt-8 space-y-3.5">
            {c.features.map((f, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.08, ease: EASE }}
                className="flex items-start gap-3 text-lake-deep/80 text-sm sm:text-base"
              >
                <span className="mt-0.5 w-5 h-5 rounded-full bg-teal/15 border border-teal/40 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-teal" />
                </span>
                {f}
              </motion.li>
            ))}
          </ul>
          <p className="mt-6 font-mono text-[11px] tracking-wide text-terracotta">{c.note}</p>
          <Link
            data-testid="cabinati-booking-cta"
            to="/prenota?service=sailing_course_1"
            className="mt-8 inline-flex items-center gap-2 bg-lake-deep text-glacier px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-teal transition-colors duration-300"
          >
            {c.cta}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="lg:col-span-7 grid grid-cols-2 gap-4">
          <motion.figure
            data-testid="cabinati-photo-main"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="col-span-2 relative overflow-hidden rounded-md aspect-[16/9] group"
          >
            <img
              src={IMG_LARGE}
              alt={c.captions[0]}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-lake-deep/80 to-transparent px-4 pb-3 pt-10">
              <p className="font-mono text-[11px] tracking-[0.15em] text-glacier/90">{c.captions[0]}</p>
            </figcaption>
          </motion.figure>
          <motion.figure
            data-testid="cabinati-photo-a"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
            className="relative overflow-hidden rounded-md aspect-[4/5] lg:-mt-4 group"
          >
            <img
              src={IMG_A}
              alt={c.captions[1]}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-lake-deep/80 to-transparent px-4 pb-3 pt-10">
              <p className="font-mono text-[11px] tracking-[0.15em] text-glacier/90">{c.captions[1]}</p>
            </figcaption>
          </motion.figure>
          <motion.figure
            data-testid="cabinati-photo-b"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.22, ease: EASE }}
            className="relative overflow-hidden rounded-md aspect-[4/5] lg:mt-4 group"
          >
            <img
              src={IMG_B}
              alt={c.captions[2]}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-lake-deep/80 to-transparent px-4 pb-3 pt-10">
              <p className="font-mono text-[11px] tracking-[0.15em] text-glacier/90">{c.captions[2]}</p>
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}
